import React, { useState, useEffect, useRef, useMemo } from 'react'
import Sidebar from 'components/Sidebar'
import Avatar from 'components/Avatar'
import { useUser } from 'utils/userContext'
import { supabase } from 'utils/supabaseClient'
import Editor from '@monaco-editor/react'

// Import lesson components for preview
import MultiChoice from "components/challenges/multiChoice/MultiChoice"
import InputBox from "components/challenges/inputBox/InputBox"
import TextBox from "components/challenges/textBox/TextBox"
import MultiChoiceSplit from "components/challenges/multiChoiceSplit/MultiChoiceSplit"
import Match from "components/challenges/match/Match"
import Intro from "components/intro/Intro"

import * as lessonParser from 'utils/lessonParser'

// Simple error boundary for preview
class PreviewErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        console.log('ErrorBoundary catching error:', error)
        return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
        console.error('Preview error caught by boundary:', error, errorInfo)
        // Force update to ensure we show the error UI
        this.forceUpdate()
    }

    componentDidUpdate(prevProps) {
        // Reset error state when key changes
        if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
            console.log('Resetting error boundary')
            this.setState({ hasError: false, error: null })
        }
    }

    render() {
        console.log('ErrorBoundary render, hasError:', this.state.hasError)
        if (this.state.hasError) {
            const errorMsg = this.state.error?.message || this.state.error?.toString() || 'Component failed to render'

            // Detect common errors and provide helpful hints
            let hint = 'Check your JSON structure matches the expected format for this challenge type.'
            if (errorMsg.includes("Cannot read properties of undefined (reading 'map')")) {
                hint = 'Missing required array in "data" (e.g., "segments" for InputBox, "pairs" for Match).'
            } else if (errorMsg.includes("Cannot read properties of undefined")) {
                hint = 'Missing required field in "data" object. Check the challenge type requirements.'
            } else if (errorMsg.includes('segments')) {
                hint = 'InputBox requires "data.segments" array with "placeholder" and "input" objects.'
            } else if (errorMsg.includes('choices')) {
                hint = 'MultiChoice requires "data.choices" with "correct" and "incorrect" arrays.'
            } else if (errorMsg.includes('pairs')) {
                hint = 'Match requires "data.pairs" array with objects containing "left" and "right" fields.'
            }

            return (
                <div className='h-full flex items-center justify-center p-6 bg-background'>
                    <div className='text-warning text-lg p-6 bg-warning-darker bg-opacity-10 rounded-lg border-2 border-warning max-w-2xl'>
                        <div className='font-bold mb-3 text-xl'>❌ Preview Component Error</div>
                        <div className='text-sm mb-3'>
                            <span className='font-semibold'>Error:</span>
                            <div className='font-mono bg-code p-2 rounded mt-1 text-xs'>
                                {errorMsg}
                            </div>
                        </div>
                        <div className='text-sm text-text-light bg-background-light p-3 rounded'>
                            <span className='font-semibold'>💡 Hint:</span> {hint}
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

const STORAGE_KEY_PREFIX = 'kodeo.dev.adminCreator'
const STORAGE_CONCEPTS = `${STORAGE_KEY_PREFIX}.concepts`

// Default template for new challenges
const DEFAULT_CHALLENGE = {
    "type": "MultiChoice",
    "question": [
        "What is **2 * %0%**?"
    ],
    "variables": [
        {
            "type": "gen",
            "func": "genInt",
            "params": {
                "min": 1,
                "max": 9
            }
        }
    ],
    "data": {
        "choices": {
            "correct": {
                "type": "gen",
                "func": "genExpression",
                "params": {
                    "expression": "2 * %0%"
                }
            },
            "incorrect": [{ "type": "value", "value": "%0%" }, { "type": "gen", "func": "genExpression", "params": { "expression": "3 * %0%" } }]
        }
    }
}

const DEFAULT_INTRO = {
    "type": "Intro",
    "data": {
        "content": "# Introduction\n\nThis is a new intro. Edit the JSON to customize!"
    }
}

// Helper to generate unique ID
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2)

// Helper to load concepts from localStorage
const loadConcepts = () => {
    const stored = localStorage.getItem(STORAGE_CONCEPTS)
    if (!stored) return []
    try {
        const parsed = JSON.parse(stored)
        console.log('Loaded concepts from localStorage:', parsed)
        return parsed
    } catch (e) {
        console.error('Failed to parse stored concepts:', e)
        return []
    }
}

// Helper to save concepts to localStorage
const saveConcepts = (concepts) => {
    console.log('Saving concepts to localStorage:', concepts)
    localStorage.setItem(STORAGE_CONCEPTS, JSON.stringify(concepts))
}

function AdminCreator() {
    const { user, loading } = useUser()
    const [concepts, setConcepts] = useState([])
    const [currentConceptId, setCurrentConceptId] = useState(null)
    const [currentChallengeId, setCurrentChallengeId] = useState(null)

    // Load concepts on mount
    useEffect(() => {
        const loaded = loadConcepts()
        setConcepts(loaded)
    }, [])

    // Save concepts whenever they change (with debounce)
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (concepts.length >= 0) {
                saveConcepts(concepts)
            }
        }, 300)
        return () => clearTimeout(timeout)
    }, [concepts])

    const currentConcept = concepts.find(c => c.id === currentConceptId)
    const currentChallenge = currentConcept?.challenges?.find(ch => ch.id === currentChallengeId)

    const createNewConcept = () => {
        const newConcept = {
            id: generateId(),
            name: 'Untitled Concept',
            description: '',
            challenges: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
        setConcepts(prev => {
            const updated = [...prev, newConcept]
            saveConcepts(updated) // Save immediately
            return updated
        })
        setCurrentConceptId(newConcept.id)
    }

    const deleteConcept = (id) => {
        if (!window.confirm('Delete this concept and all its challenges? This cannot be undone.')) return
        setConcepts(prev => {
            const updated = prev.filter(c => c.id !== id)
            saveConcepts(updated) // Save immediately
            return updated
        })
        if (currentConceptId === id) {
            setCurrentConceptId(null)
            setCurrentChallengeId(null)
        }
    }

    const renameConcept = (id, newName) => {
        setConcepts(prev => prev.map(c =>
            c.id === id ? { ...c, name: newName, updatedAt: new Date().toISOString() } : c
        ))
    }

    const updateConceptDescription = (id, newDescription) => {
        setConcepts(prev => prev.map(c =>
            c.id === id ? { ...c, description: newDescription, updatedAt: new Date().toISOString() } : c
        ))
    }

    const duplicateConcept = (id) => {
        const original = concepts.find(c => c.id === id)
        if (!original) return

        const duplicate = {
            ...original,
            id: generateId(),
            name: original.name + ' (Copy)',
            challenges: original.challenges.map(ch => ({ ...ch, id: generateId() })),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
        setConcepts(prev => {
            const updated = [...prev, duplicate]
            saveConcepts(updated) // Save immediately
            return updated
        })
    }

    const createChallenge = (conceptId, type = 'challenge') => {
        const concept = concepts.find(c => c.id === conceptId)
        if (!concept) return

        // Check if intro already exists
        if (type === 'intro' && concept.challenges?.some(ch => ch.type === 'intro')) {
            alert('Only one intro is allowed per concept')
            return
        }

        // Generate unique name
        const baseName = type === 'intro' ? 'Intro' : 'Untitled Challenge'
        let finalName = baseName
        let counter = 1

        // Check if name exists and increment
        while (concept.challenges?.some(ch => ch.name === finalName)) {
            finalName = `${baseName} (${counter})`
            counter++
        }

        const newChallenge = {
            id: generateId(),
            name: finalName,
            type: type,
            content: JSON.stringify(type === 'intro' ? DEFAULT_INTRO : DEFAULT_CHALLENGE, null, 2),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
        console.log('Creating challenge:', newChallenge)
        setConcepts(prev => prev.map(c =>
            c.id === conceptId
                ? { ...c, challenges: [...(c.challenges || []), newChallenge], updatedAt: new Date().toISOString() }
                : c
        ))
        setCurrentChallengeId(newChallenge.id)
    }

    const deleteChallenge = (conceptId, challengeId) => {
        if (!window.confirm('Delete this challenge? This cannot be undone.')) return
        setConcepts(prev => prev.map(c =>
            c.id === conceptId
                ? { ...c, challenges: c.challenges.filter(ch => ch.id !== challengeId), updatedAt: new Date().toISOString() }
                : c
        ))
        if (currentChallengeId === challengeId) {
            setCurrentChallengeId(null)
        }
    }

    const renameChallenge = (conceptId, challengeId, newName) => {
        setConcepts(prev => prev.map(c =>
            c.id === conceptId
                ? {
                    ...c,
                    challenges: c.challenges.map(ch =>
                        ch.id === challengeId ? { ...ch, name: newName, updatedAt: new Date().toISOString() } : ch
                    ),
                    updatedAt: new Date().toISOString()
                }
                : c
        ))
    }

    const updateChallengeContent = (conceptId, challengeId, newContent, generationPrompt = null) => {
        console.log('Updating challenge content:', { conceptId, challengeId, newContent: newContent.substring(0, 100) + '...', generationPrompt })
        setConcepts(prev => prev.map(c =>
            c.id === conceptId
                ? {
                    ...c,
                    challenges: c.challenges.map(ch =>
                        ch.id === challengeId
                            ? {
                                ...ch,
                                content: newContent,
                                ...(generationPrompt && { generationPrompt }), // Add generation prompt if provided
                                updatedAt: new Date().toISOString()
                            }
                            : ch
                    ),
                    updatedAt: new Date().toISOString()
                }
                : c
        ))
    }

    const duplicateChallenge = (conceptId, challengeId) => {
        const concept = concepts.find(c => c.id === conceptId)
        const original = concept?.challenges?.find(ch => ch.id === challengeId)
        if (!original) return

        const duplicate = {
            ...original,
            id: generateId(),
            name: original.name + ' (Copy)',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
        setConcepts(prev => prev.map(c =>
            c.id === conceptId
                ? { ...c, challenges: [...c.challenges, duplicate], updatedAt: new Date().toISOString() }
                : c
        ))
    }

    const importFromLessons = (unitIdx, conceptIdx, lessonsData) => {
        const lessonConcept = lessonsData[unitIdx]?.concepts?.[conceptIdx]
        if (!lessonConcept) return

        const newConcept = {
            id: generateId(),
            name: `${lessonsData[unitIdx].unit} - ${lessonConcept.concept}`,
            challenges: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }

        // Add intro if exists
        if (lessonConcept.intro) {
            newConcept.challenges.push({
                id: generateId(),
                name: 'Intro',
                type: 'intro',
                content: JSON.stringify(lessonConcept.intro, null, 2),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            })
        }

        // Add all challenges
        if (lessonConcept.challenges) {
            lessonConcept.challenges.forEach((ch, idx) => {
                newConcept.challenges.push({
                    id: generateId(),
                    name: `Challenge ${idx + 1}`,
                    type: 'challenge',
                    content: JSON.stringify(ch, null, 2),
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                })
            })
        }

        setConcepts(prev => {
            const updated = [...prev, newConcept]
            saveConcepts(updated) // Save immediately
            return updated
        })
    }

    if (loading || !user?.is_admin) {
        return (
            <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
                <Sidebar />
                <div className="flex-grow h-full bg-background text-text flex items-center justify-center">
                    <h1 className="text-2xl">Access Denied</h1>
                </div>
            </div>
        )
    }

    // Show challenge editor if a challenge is selected
    if (currentConceptId && currentChallengeId && currentChallenge && currentConcept) {
        return (
            <ChallengeEditor
                concept={currentConcept}
                challenge={currentChallenge}
                onBack={() => setCurrentChallengeId(null)}
                onUpdate={(newContent, generationPrompt) => updateChallengeContent(currentConceptId, currentChallengeId, newContent, generationPrompt)}
                onRename={(newName) => renameChallenge(currentConceptId, currentChallengeId, newName)}
            />
        )
    }

    // Show concept detail if a concept is selected
    if (currentConceptId && currentConcept) {
        return (
            <ConceptDetailView
                concept={currentConcept}
                allConcepts={concepts}
                onBack={() => setCurrentConceptId(null)}
                onCreateChallenge={(type) => createChallenge(currentConceptId, type)}
                onSelectChallenge={setCurrentChallengeId}
                onDeleteChallenge={(challengeId) => deleteChallenge(currentConceptId, challengeId)}
                onRenameChallenge={(challengeId, newName) => renameChallenge(currentConceptId, challengeId, newName)}
                onDuplicateChallenge={(challengeId) => duplicateChallenge(currentConceptId, challengeId)}
                onRenameConcept={(newName) => renameConcept(currentConceptId, newName)}
                onUpdateDescription={(newDescription) => updateConceptDescription(currentConceptId, newDescription)}
            />
        )
    }

    // Show list view
    return (
        <ConceptListView
            concepts={concepts}
            onSelectConcept={setCurrentConceptId}
            onCreateNew={createNewConcept}
            onDelete={deleteConcept}
            onRename={renameConcept}
            onDuplicate={duplicateConcept}
            onImport={importFromLessons}
        />
    )
}

// List View Component
function ConceptListView({ concepts, onSelectConcept, onCreateNew, onDelete, onRename, onDuplicate, onImport }) {
    const [showImport, setShowImport] = useState(false)
    const [allLessons, setAllLessons] = useState([])
    const [editingId, setEditingId] = useState(null)
    const [editName, setEditName] = useState('')

    useEffect(() => {
        const fetchLessons = async () => {
            try {
                const response = await fetch('/lessons.json')
                const data = await response.json()
                setAllLessons(data)
            } catch (error) {
                console.error('Failed to load lessons:', error)
            }
        }
        fetchLessons()
    }, [])

    const startRename = (concept) => {
        setEditingId(concept.id)
        setEditName(concept.name)
    }

    const finishRename = () => {
        if (editingId && editName.trim()) {
            onRename(editingId, editName.trim())
        }
        setEditingId(null)
        setEditName('')
    }

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
            <Sidebar />
            <div className='flex-grow h-full bg-background text-text overflow-hidden flex flex-col'>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-border">
                    <div>
                        <h1 className='font-bold text-[1.8rem] lg:text-[2rem]'>Lesson Creator</h1>
                        <p className='text-text-light text-sm'>{concepts.length} saved concept{concepts.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className='flex items-center gap-3'>
                        <button
                            onClick={() => setShowImport(true)}
                            className='px-4 py-2 bg-background-light hover:bg-border text-text font-medium rounded-lg transition-colors border-2 border-border'
                        >
                            📥 Import from Lessons
                        </button>
                        <button
                            onClick={onCreateNew}
                            className='px-4 py-2 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-colors'
                        >
                            ✨ New Concept
                        </button>
                        <Avatar size={"xs"} showRings={false} dropdown={true} />
                    </div>
                </div>

                {/* Concepts Grid */}
                <div className='flex-grow overflow-y-auto p-6'>
                    {concepts.length === 0 ? (
                        <div className='flex flex-col items-center justify-center h-full text-text-light'>
                            <svg className='w-24 h-24 mb-4 opacity-30' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" fill="currentColor" />
                            </svg>
                            <h2 className='text-xl font-bold mb-2'>No concepts yet</h2>
                            <p className='text-sm mb-4'>Create a new concept or import from existing lessons</p>
                            <button
                                onClick={onCreateNew}
                                className='px-6 py-3 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-colors'
                            >
                                Create Your First Concept
                            </button>
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start'>
                            {concepts.map(concept => (
                                <div
                                    key={concept.id}
                                    className='bg-background-light border-2 border-border hover:border-primary rounded-xl p-4 transition-all cursor-pointer group relative flex flex-col'
                                    onClick={() => onSelectConcept(concept.id)}
                                >
                                    <div className='flex items-start justify-between gap-3 mb-2 min-h-[2rem]'>
                                        {editingId === concept.id ? (
                                            <input
                                                type="text"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onBlur={finishRename}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') finishRename()
                                                    if (e.key === 'Escape') {
                                                        setEditingId(null)
                                                        setEditName('')
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                className='flex-1 bg-background border-2 border-primary rounded px-2 py-1 font-bold text-lg outline-none'
                                            />
                                        ) : (
                                            <h3 className='flex-1 font-bold text-lg group-hover:text-primary transition-colors break-words'>
                                                {concept.name}
                                            </h3>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                startRename(concept)
                                            }}
                                            className='opacity-0 group-hover:opacity-100 p-1.5 hover:bg-border rounded transition-all text-sm flex-shrink-0'
                                            title="Rename"
                                        >
                                            ✏️
                                        </button>
                                    </div>

                                    <p className='text-xs text-text-light mb-3'>
                                        {concept.challenges?.length || 0} challenge{concept.challenges?.length !== 1 ? 's' : ''} • Updated {new Date(concept.updatedAt).toLocaleDateString()}
                                    </p>

                                    <div className='flex gap-2 pt-3 border-t border-border mt-auto'>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onDuplicate(concept.id)
                                            }}
                                            className='flex-1 px-3 py-1.5 bg-background hover:bg-border text-text text-xs font-medium rounded transition-colors'
                                            title="Duplicate"
                                        >
                                            Duplicate
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onDelete(concept.id)
                                            }}
                                            className='px-3 py-1.5 bg-warning-darker hover:bg-warning text-white text-xs font-medium rounded transition-colors'
                                            title="Delete"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Import Modal */}
            {showImport && (
                <div
                    className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowImport(false)
                    }}
                >
                    <div className='bg-background-pop rounded-2xl p-6 max-w-4xl max-h-[80vh] w-full mx-4 border-2 border-border overflow-auto'>
                        <h2 className='text-2xl font-bold mb-4 text-text'>Import from Lessons</h2>
                        <div className='space-y-4'>
                            {allLessons.map((unit, unitIdx) => (
                                <div key={unitIdx} className='border-2 border-border rounded-xl p-4'>
                                    <h3 className='font-bold text-lg text-primary mb-3'>
                                        Unit {unitIdx + 1}: {unit.unit}
                                    </h3>
                                    <div className='grid grid-cols-2 md:grid-cols-3 gap-2'>
                                        {unit.concepts?.map((concept, conceptIdx) => (
                                            <button
                                                key={conceptIdx}
                                                onClick={() => {
                                                    onImport(unitIdx, conceptIdx, allLessons)
                                                    setShowImport(false)
                                                }}
                                                className='px-3 py-2 bg-background hover:bg-border text-text text-sm font-medium rounded-lg transition-colors text-left'
                                            >
                                                {conceptIdx + 1}. {concept.concept}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='flex justify-end mt-6 pt-4 border-t border-border'>
                            <button
                                onClick={() => setShowImport(false)}
                                className='px-6 py-2 bg-background hover:bg-border text-text font-bold rounded-lg transition-colors'
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

// Concept Description Component with AI generation
function ConceptDescription({ description, conceptName, onUpdate }) {
    const [isExpanded, setIsExpanded] = useState(!!description)
    const [localDescription, setLocalDescription] = useState(description)
    const [showPrompt, setShowPrompt] = useState(false)
    const [customPrompt, setCustomPrompt] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)
    const { user } = useUser()

    // Update local state when prop changes
    useEffect(() => {
        setLocalDescription(description)
    }, [description])

    // Debounced save
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (localDescription !== description) {
                onUpdate(localDescription)
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [localDescription])

    const handleGenerate = async () => {
        setIsGenerating(true)
        try {
            // Get token from session
            const { data: sessionData, error: sessionErr } = await supabase.auth.getSession()
            if (sessionErr || !sessionData.session?.access_token) {
                alert('Authentication required. Please log in again.')
                return
            }
            const token = sessionData.session.access_token

            console.log('Generating concept description for:', conceptName)
            const response = await fetch('/.netlify/functions/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: 'concept-description',
                    conceptName,
                    customPrompt: customPrompt.trim()
                })
            })

            let data
            try {
                data = await response.json()
            } catch (e) {
                const text = await response.text()
                console.error('Response is not JSON:', text)
                if (text.includes('TimeoutError') || text.includes('Task timed out')) {
                    alert('Request timed out. The AI is taking too long to respond. Please try again with a simpler request.')
                } else {
                    alert(`Server error: ${text}`)
                }
                return
            }

            console.log('Generation response:', data)

            if (response.ok && data.success) {
                setLocalDescription(data.content)
                setShowPrompt(false)
                setCustomPrompt('')
            } else {
                console.error('Generation failed:', data)
                alert(`Generation failed: ${data.error || 'Unknown error'}`)
            }
        } catch (error) {
            console.error('Generation error:', error)
            alert(`Failed to generate description: ${error.message}`)
        } finally {
            setIsGenerating(false)
        }
    }

    return (
        <div className='border-b-2 border-border bg-background-light'>
            <div className='px-6 py-3'>
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className='flex items-center gap-2 text-text-light hover:text-text transition-colors text-sm font-medium w-full'
                >
                    <span>{isExpanded ? '▼' : '▶'}</span>
                    <span>Concept Description</span>
                    {description && <span className='text-xs'>({description.length} chars)</span>}
                </button>

                {isExpanded && (
                    <div className='mt-3 relative'>
                        <textarea
                            value={localDescription}
                            onChange={(e) => setLocalDescription(e.target.value)}
                            placeholder="Describe what this concept teaches, learning objectives, prerequisites, key topics, and real-world applications..."
                            className='w-full h-32 p-3 bg-background border-2 border-border rounded-lg text-text resize-none outline-none focus:border-primary transition-colors text-sm'
                        />
                        <div className='flex items-center justify-end gap-2 mt-2'>
                            <button
                                onClick={() => setShowPrompt(!showPrompt)}
                                disabled={isGenerating}
                                className='px-3 py-1.5 bg-primary hover:bg-opacity-80 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                {isGenerating ? '⏳ Generating...' : '✨ AI Generate'}
                            </button>
                        </div>

                        {/* Prompt popup */}
                        {showPrompt && !isGenerating && (
                            <div className='mt-2 p-3 bg-background border-2 border-primary rounded-lg'>
                                <label className='text-xs font-medium text-text-light mb-1 block'>
                                    Additional instructions (optional):
                                </label>
                                <input
                                    type="text"
                                    value={customPrompt}
                                    onChange={(e) => setCustomPrompt(e.target.value)}
                                    placeholder="e.g., Focus on beginners, include comparisons to JavaScript..."
                                    className='w-full px-2 py-1.5 bg-background-light border border-border rounded text-sm outline-none focus:border-primary'
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleGenerate()
                                        if (e.key === 'Escape') setShowPrompt(false)
                                    }}
                                    autoFocus
                                />
                                <div className='flex gap-2 mt-2'>
                                    <button
                                        onClick={handleGenerate}
                                        className='flex-1 px-3 py-1.5 bg-accent hover:bg-opacity-80 text-white text-xs font-bold rounded transition-colors'
                                    >
                                        Generate
                                    </button>
                                    <button
                                        onClick={() => setShowPrompt(false)}
                                        className='px-3 py-1.5 bg-background-light hover:bg-border text-text text-xs font-medium rounded transition-colors'
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// Concept Detail View Component (shows challenges within a concept)
function ConceptDetailView({ concept, onBack, onCreateChallenge, onSelectChallenge, onDeleteChallenge, onRenameChallenge, onDuplicateChallenge, onRenameConcept, onUpdateDescription, allConcepts }) {
    const [editingId, setEditingId] = useState(null)
    const [editName, setEditName] = useState('')
    const [editingConceptName, setEditingConceptName] = useState(false)
    const [conceptName, setConceptName] = useState(concept.name)
    const [showIdeaModal, setShowIdeaModal] = useState(false)
    const [generatedIdea, setGeneratedIdea] = useState('')
    const [isGeneratingIdea, setIsGeneratingIdea] = useState(false)

    const startRename = (challenge) => {
        setEditingId(challenge.id)
        setEditName(challenge.name)
    }

    const finishRename = () => {
        if (editingId && editName.trim()) {
            onRenameChallenge(editingId, editName.trim())
        }
        setEditingId(null)
        setEditName('')
    }

    const finishConceptRename = () => {
        if (conceptName.trim()) {
            onRenameConcept(conceptName.trim())
        }
        setEditingConceptName(false)
    }

    const handleGenerateIdea = async () => {
        setIsGeneratingIdea(true)
        setGeneratedIdea('')

        try {
            // Get token from session
            const { data: sessionData, error: sessionErr } = await supabase.auth.getSession()
            if (sessionErr || !sessionData.session?.access_token) {
                alert('Authentication required. Please log in again.')
                setIsGeneratingIdea(false)
                return
            }
            const token = sessionData.session.access_token

            // Fetch previous concepts from actual lessons.json
            let previousConcepts = []
            try {
                const lessonsResponse = await fetch('/lessons.json')
                const lessonsData = await lessonsResponse.json()

                // Flatten all concepts from all units (exclude DEVELOPMENT unit)
                const allLessonConcepts = []
                lessonsData.forEach(unit => {
                    // Skip DEVELOPMENT unit
                    if (unit.unit === 'DEVELOPMENT') return

                    unit.concepts?.forEach(c => {
                        allLessonConcepts.push(`${unit.unit}: ${c.concept}`)
                    })
                })

                // Find current concept in lessons and get all concepts before it
                const currentConceptName = concept.name
                const currentIndex = allLessonConcepts.findIndex(name =>
                    name.toLowerCase().includes(currentConceptName.toLowerCase()) ||
                    currentConceptName.toLowerCase().includes(name.toLowerCase())
                )

                if (currentIndex > 0) {
                    previousConcepts = allLessonConcepts.slice(0, currentIndex)
                } else {
                    // If not found in lessons, use all lesson concepts as context
                    previousConcepts = allLessonConcepts
                }
            } catch (e) {
                console.error('Failed to fetch lessons.json:', e)
                // Fallback: use empty array
                previousConcepts = []
            }

            // Build challenges context from ALL past challenges (exclude intro)
            const challengesContext = []

            // Process only non-intro challenges
            concept.challenges
                ?.filter(ch => ch.type !== 'intro')
                .forEach((ch, idx) => {
                    const challengeNum = idx + 1

                    if (ch.generationPrompt) {
                        challengesContext.push(`Challenge ${challengeNum}: ${ch.generationPrompt}`)
                    } else {
                        // Fallback to JSON description
                        try {
                            const parsed = JSON.parse(ch.content)
                            const questionText = Array.isArray(parsed.question)
                                ? parsed.question.join(' ')
                                : parsed.question || 'No question text'
                            challengesContext.push(`Challenge ${challengeNum}: ${parsed.type} - ${questionText}`)
                        } catch (e) {
                            challengesContext.push(`Challenge ${challengeNum}: (Unable to parse)`)
                        }
                    }
                })

            const response = await fetch('/.netlify/functions/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: 'idea',
                    conceptName: concept.name,
                    conceptDescription: concept.description || '',
                    previousConcepts: previousConcepts,
                    challengesContext: challengesContext.join('\n\n')
                })
            })

            const data = await response.json()

            if (response.ok && data.success) {
                setGeneratedIdea(data.content)
            } else {
                alert(`Error: ${data.error || 'Unknown error'}`)
            }
        } catch (error) {
            console.error('Idea generation error:', error)
            alert(`Failed to generate idea: ${error.message}`)
        } finally {
            setIsGeneratingIdea(false)
        }
    }

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
            <Sidebar />
            <div className='flex-grow h-full bg-background text-text overflow-hidden flex flex-col'>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-border">
                    <div className='flex items-center gap-4'>
                        <button
                            onClick={onBack}
                            className='p-2 hover:bg-border rounded-lg transition-colors'
                            title="Back to concepts"
                        >
                            ← Back
                        </button>
                        <div>
                            {editingConceptName ? (
                                <input
                                    type="text"
                                    value={conceptName}
                                    onChange={(e) => setConceptName(e.target.value)}
                                    onBlur={finishConceptRename}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') finishConceptRename()
                                        if (e.key === 'Escape') {
                                            setEditingConceptName(false)
                                            setConceptName(concept.name)
                                        }
                                    }}
                                    autoFocus
                                    className='bg-background border-2 border-primary rounded px-2 py-1 font-bold text-xl outline-none'
                                />
                            ) : (
                                <h1
                                    className='font-bold text-xl cursor-pointer hover:text-primary transition-colors'
                                    onClick={() => setEditingConceptName(true)}
                                >
                                    {concept.name}
                                </h1>
                            )}
                            <p className='text-xs text-text-light'>{concept.challenges?.length || 0} challenge{concept.challenges?.length !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <div className='flex items-center gap-3'>
                        <button
                            onClick={() => onCreateChallenge('intro')}
                            disabled={concept.challenges?.some(ch => ch.type === 'intro')}
                            className='px-4 py-2 bg-background-light hover:bg-border text-text font-medium rounded-lg transition-colors border-2 border-border disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            📄 New Intro
                        </button>
                        <button
                            onClick={() => onCreateChallenge('challenge')}
                            className='px-4 py-2 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-colors'
                        >
                            ✨ New Challenge
                        </button>
                        {concept.challenges && concept.challenges.length > 0 && (
                            <button
                                onClick={() => setShowIdeaModal(true)}
                                disabled={isGeneratingIdea}
                                className='px-4 py-2 bg-accent hover:bg-opacity-80 text-white font-medium rounded-lg transition-colors disabled:opacity-50'
                            >
                                {isGeneratingIdea ? '⏳' : '💡'} Generate Idea
                            </button>
                        )}
                        <Avatar size={"xs"} showRings={false} dropdown={true} />
                    </div>
                </div>

                {/* Concept Description Section */}
                <ConceptDescription
                    description={concept.description || ''}
                    conceptName={concept.name}
                    onUpdate={onUpdateDescription}
                />

                {/* Challenges Grid */}
                <div className='flex-grow overflow-y-auto p-6'>
                    {!concept.challenges || concept.challenges.length === 0 ? (
                        <div className='flex flex-col items-center justify-center h-full text-text-light'>
                            <svg className='w-24 h-24 mb-4 opacity-30' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor" />
                            </svg>
                            <h2 className='text-xl font-bold mb-2'>No challenges yet</h2>
                            <p className='text-sm mb-4'>Create your first challenge or intro</p>
                            <div className='flex gap-3'>
                                <button
                                    onClick={() => onCreateChallenge('intro')}
                                    disabled={concept.challenges?.some(ch => ch.type === 'intro')}
                                    className='px-6 py-3 bg-background-light hover:bg-border text-text font-bold rounded-lg transition-colors border-2 border-border disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    Create Intro
                                </button>
                                <button
                                    onClick={() => onCreateChallenge('challenge')}
                                    className='px-6 py-3 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-colors'
                                >
                                    Create Challenge
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start'>
                            {/* Sort challenges: intro first, then rest by creation date */}
                            {[...concept.challenges]
                                .sort((a, b) => {
                                    if (a.type === 'intro') return -1
                                    if (b.type === 'intro') return 1
                                    return new Date(a.createdAt) - new Date(b.createdAt)
                                })
                                .map(challenge => (
                                    <div
                                        key={challenge.id}
                                        className='bg-background-light border-2 border-border hover:border-primary rounded-xl p-4 transition-all cursor-pointer group flex flex-col'
                                        onClick={() => onSelectChallenge(challenge.id)}
                                    >
                                        <div className='flex items-start justify-between gap-3 mb-2 min-h-[2rem]'>
                                            <div className='flex items-center gap-2 flex-1'>
                                                <span className='text-lg flex-shrink-0'>{challenge.type === 'intro' ? '📄' : '🎯'}</span>
                                                {editingId === challenge.id ? (
                                                    <input
                                                        type="text"
                                                        value={editName}
                                                        onChange={(e) => setEditName(e.target.value)}
                                                        onBlur={finishRename}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') finishRename()
                                                            if (e.key === 'Escape') {
                                                                setEditingId(null)
                                                                setEditName('')
                                                            }
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        autoFocus
                                                        className='flex-1 bg-background border-2 border-primary rounded px-2 py-1 font-bold text-base outline-none'
                                                    />
                                                ) : (
                                                    <h4 className='flex-1 font-bold text-base group-hover:text-primary transition-colors break-words'>
                                                        {challenge.name}
                                                    </h4>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    startRename(challenge)
                                                }}
                                                className='opacity-0 group-hover:opacity-100 p-1.5 hover:bg-border rounded transition-all text-sm flex-shrink-0'
                                                title="Rename"
                                            >
                                                ✏️
                                            </button>
                                        </div>

                                        <p className='text-xs text-text-light mb-3'>
                                            {challenge.type === 'intro' ? 'Introduction' : 'Challenge'} • {new Date(challenge.updatedAt).toLocaleDateString()}
                                        </p>

                                        <div className='flex gap-2 pt-3 border-t border-border mt-auto'>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onDuplicateChallenge(challenge.id)
                                                }}
                                                className='flex-1 px-3 py-1.5 bg-background hover:bg-border text-text text-xs font-medium rounded transition-colors'
                                                title="Duplicate"
                                            >
                                                Duplicate
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onDeleteChallenge(challenge.id)
                                                }}
                                                className='px-3 py-1.5 bg-warning-darker hover:bg-warning text-white text-xs font-medium rounded transition-colors'
                                                title="Delete"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {/* Idea Generation Modal */}
                {showIdeaModal && (
                    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50' onClick={() => setShowIdeaModal(false)}>
                        <div className='bg-background border-2 border-accent rounded-xl p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto' onClick={(e) => e.stopPropagation()}>
                            <div className='flex items-center justify-between mb-4'>
                                <h2 className='text-2xl font-bold text-text'>💡 Challenge Idea</h2>
                                <button
                                    onClick={() => setShowIdeaModal(false)}
                                    className='p-2 hover:bg-border rounded transition-colors'
                                >
                                    ✕
                                </button>
                            </div>

                            {!generatedIdea && !isGeneratingIdea && (
                                <div className='text-center py-8'>
                                    <p className='text-text-light mb-4'>Generate a suggestion for the next challenge based on existing challenges in this concept.</p>
                                    <button
                                        onClick={handleGenerateIdea}
                                        className='px-6 py-3 bg-accent hover:bg-opacity-80 text-white font-bold rounded-lg transition-colors'
                                    >
                                        Generate Idea
                                    </button>
                                </div>
                            )}

                            {isGeneratingIdea && (
                                <div className='text-center py-8'>
                                    <div className='text-4xl mb-4'>⏳</div>
                                    <p className='text-text-light'>Generating idea...</p>
                                </div>
                            )}

                            {generatedIdea && (
                                <div className='bg-background-light border border-border rounded-lg p-4 mb-4'>
                                    <pre className='whitespace-pre-wrap text-sm text-text font-mono'>{generatedIdea}</pre>
                                </div>
                            )}

                            <div className='flex flex-col gap-3 mt-6'>
                                {generatedIdea && (
                                    <>
                                        <button
                                            onClick={async () => {
                                                // Store the idea as the pending prompt BEFORE creating challenge
                                                window._pendingIdeaPrompt = generatedIdea

                                                // Create new challenge with the idea as the prompt
                                                await onCreateChallenge('challenge')

                                                // Close the modal
                                                setShowIdeaModal(false)
                                            }}
                                            className='w-full px-4 py-3 bg-primary hover:bg-opacity-80 text-white font-bold rounded-lg transition-colors'
                                        >
                                            ✨ Create Challenge from Idea
                                        </button>
                                        <div className='flex gap-3'>
                                            <button
                                                onClick={handleGenerateIdea}
                                                disabled={isGeneratingIdea}
                                                className='flex-1 px-4 py-2 bg-accent hover:bg-opacity-80 text-white font-medium rounded-lg transition-colors disabled:opacity-50'
                                            >
                                                Regenerate
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowIdeaModal(false)
                                                    setGeneratedIdea('')
                                                }}
                                                className='flex-1 px-4 py-2 bg-background-light hover:bg-border text-text font-medium rounded-lg transition-colors'
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </>
                                )}
                                {!generatedIdea && (
                                    <button
                                        onClick={() => {
                                            setShowIdeaModal(false)
                                            setGeneratedIdea('')
                                        }}
                                        className='w-full px-4 py-2 bg-background-light hover:bg-border text-text font-medium rounded-lg transition-colors'
                                    >
                                        Close
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

// Challenge Editor Component (edits a single challenge)
function ChallengeEditor({ concept, challenge, onBack, onUpdate, onRename }) {
    const [activeTab, setActiveTab] = useState('code')
    const [jsonError, setJsonError] = useState(null)
    const [parsedChallenge, setParsedChallenge] = useState(null)
    const [editingName, setEditingName] = useState(false)
    const [challengeName, setChallengeName] = useState(challenge.name)
    const [editorKey, setEditorKey] = useState(0) // Force remount when switching challenges
    const [showGeneratePrompt, setShowGeneratePrompt] = useState(false)
    const [customPrompt, setCustomPrompt] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)
    const [triggerGenerate, setTriggerGenerate] = useState(false)
    const { user } = useUser()

    const editorRef = useRef(null)
    const currentContentRef = useRef(challenge.content) // Track current content without causing re-renders

    // When switching to a different challenge, remount the editor
    useEffect(() => {
        currentContentRef.current = challenge.content
        setChallengeName(challenge.name)
        setEditorKey(prev => prev + 1) // Force editor to remount with new content

        // Check for pending idea prompt when switching to this challenge
        if (window._pendingIdeaPrompt) {
            const ideaPrompt = window._pendingIdeaPrompt
            delete window._pendingIdeaPrompt

            // Set the custom prompt and trigger generation
            setCustomPrompt(ideaPrompt)
            setTriggerGenerate(true)
        }
    }, [challenge.id])

    // Handle editor updates - just save to ref and parse for preview
    const handleEditorUpdate = (value) => {
        currentContentRef.current = value

        // Parse JSON for preview
        try {
            const parsed = JSON.parse(value)

            // Validate challenge structure
            const errors = []
            if (!parsed.type) {
                errors.push('Missing "type" field')
            }
            // For intro type, validate "data" field exists
            if (parsed.type === 'Intro' && !parsed.data) {
                errors.push('Missing "data" field (required for Intro type)')
            }
            // For non-intro types, validate "question" field exists
            if (parsed.type !== 'Intro' && !parsed.question) {
                errors.push('Missing "question" field (required for challenge types)')
            }

            if (errors.length > 0) {
                setJsonError(`Validation errors: ${errors.join(', ')}`)
                setParsedChallenge(null)
            } else {
                setParsedChallenge(parsed)
                setJsonError(null)
            }
        } catch (e) {
            setJsonError(`JSON Parse Error: ${e.message}`)
            setParsedChallenge(null)
        }
    }

    // Initial parse
    useEffect(() => {
        try {
            const parsed = JSON.parse(challenge.content)
            setParsedChallenge(parsed)
            setJsonError(null)
        } catch (e) {
            setJsonError(e.message)
            setParsedChallenge(null)
        }
    }, [challenge.id])

    // Debounced save to parent
    useEffect(() => {
        const interval = setInterval(() => {
            if (currentContentRef.current !== challenge.content) {
                onUpdate(currentContentRef.current)
            }
        }, 1000) // Save every second if changed

        return () => clearInterval(interval)
    }, [challenge.content, onUpdate])

    const finishRename = () => {
        if (challengeName.trim()) {
            onRename(challengeName.trim())
        }
        setEditingName(false)
    }

    const handleGenerate = async () => {
        setIsGenerating(true)
        setShowGeneratePrompt(true) // Show prompt area during generation
        setTriggerGenerate(false) // Reset trigger flag

        try {
            // Get token from session
            const { data: sessionData, error: sessionErr } = await supabase.auth.getSession()
            if (sessionErr || !sessionData.session?.access_token) {
                alert('Authentication required. Please log in again.')
                setIsGenerating(false)
                return
            }
            const token = sessionData.session.access_token

            const currentChallengeType = parsedChallenge?.type || challenge.type || 'MultiChoice'
            console.log('Generating challenge:', { currentType: currentChallengeType, conceptName: concept.name, prompt: customPrompt })

            // If current type is Intro, force type to 'intro', otherwise 'challenge'
            const requestType = currentChallengeType === 'Intro' ? 'intro' : 'challenge'

            const response = await fetch('/.netlify/functions/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: requestType,
                    conceptName: concept.name,
                    conceptDescription: concept.description || '',
                    currentChallengeType: currentChallengeType,
                    customPrompt: customPrompt.trim(),
                    examples: '' // TODO: Could fetch examples from lessons.json
                })
            })

            let data
            try {
                data = await response.json()
            } catch (e) {
                const text = await response.text()
                console.error('Response is not JSON:', text)
                if (text.includes('TimeoutError') || text.includes('Task timed out')) {
                    alert('Request timed out. The AI is taking too long to respond. Please try again or simplify your request.')
                } else {
                    alert(`Server error: ${text.substring(0, 200)}`)
                }
                setIsGenerating(false)
                return
            }

            console.log('Generation response:', data)

            if (response.ok && data.success) {
                // ALWAYS save the generation prompt (even if empty, save a default)
                const generationPromptText = customPrompt.trim() || `Generated ${currentChallengeType} for concept: ${concept.name}`

                // Try to parse and format the generated JSON
                try {
                    const parsed = JSON.parse(data.content)
                    const formatted = JSON.stringify(parsed, null, 2)
                    currentContentRef.current = formatted
                    onUpdate(formatted, generationPromptText) // ALWAYS pass prompt to be saved
                    setParsedChallenge(parsed)
                    setJsonError(null)
                    setEditorKey(prev => prev + 1) // Force editor refresh
                    console.log('Successfully generated and updated challenge with prompt:', generationPromptText)
                } catch (e) {
                    console.error('Failed to parse generated JSON:', e)
                    // If it's not valid JSON, just use as-is
                    currentContentRef.current = data.content
                    onUpdate(data.content, generationPromptText) // ALWAYS pass prompt
                    setEditorKey(prev => prev + 1)
                }
                setShowGeneratePrompt(false)
                setCustomPrompt('')
            } else {
                console.error('Generation failed:', data)
                alert(`Generation failed: ${data.error || 'Unknown error'}`)
            }
        } catch (error) {
            console.error('Generation error:', error)
            alert(`Failed to generate challenge: ${error.message}`)
        } finally {
            setIsGenerating(false)
        }
    }

    // Auto-trigger generation when triggerGenerate flag is set
    useEffect(() => {
        if (triggerGenerate && customPrompt && !isGenerating) {
            // Small delay to ensure UI is ready
            const timer = setTimeout(() => {
                handleGenerate()
            }, 150)
            return () => clearTimeout(timer)
        }
    }, [triggerGenerate, customPrompt, isGenerating])

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
            <Sidebar />
            <div className='flex-grow h-full bg-background text-text overflow-hidden flex flex-col'>
                {/* Header with tabs */}
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-border">
                    <div className='flex items-center gap-4'>
                        <button
                            onClick={onBack}
                            className='p-2 hover:bg-border rounded-lg transition-colors'
                            title="Back to concept"
                        >
                            ← Back
                        </button>
                        <div>
                            <div className='flex items-center gap-2'>
                                <span className='text-lg'>{challenge.type === 'intro' ? '📄' : '🎯'}</span>
                                {editingName ? (
                                    <input
                                        type="text"
                                        value={challengeName}
                                        onChange={(e) => setChallengeName(e.target.value)}
                                        onBlur={finishRename}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') finishRename()
                                            if (e.key === 'Escape') {
                                                setEditingName(false)
                                                setChallengeName(challenge.name)
                                            }
                                        }}
                                        autoFocus
                                        className='bg-background border-2 border-primary rounded px-2 py-1 font-bold text-xl outline-none'
                                    />
                                ) : (
                                    <h1
                                        className='font-bold text-xl cursor-pointer hover:text-primary transition-colors'
                                        onClick={() => setEditingName(true)}
                                    >
                                        {challenge.name}
                                    </h1>
                                )}
                            </div>
                            <p className='text-xs text-text-light'>{concept.name} • {challenge.type === 'intro' ? 'Intro' : 'Challenge'}</p>
                        </div>
                    </div>
                    <div className='flex items-center gap-4'>
                        {/* Tab Switcher */}
                        <div className='flex bg-background-light border-2 border-border rounded-lg p-1 gap-1'>
                            <button
                                onClick={() => setActiveTab('code')}
                                className={`px-6 py-2 font-semibold rounded-md transition-all ${activeTab === 'code'
                                    ? 'bg-primary text-white shadow-sm'
                                    : 'text-text-light hover:text-text hover:bg-background'
                                    }`}
                            >
                                Code
                            </button>
                            <button
                                onClick={() => setActiveTab('preview')}
                                className={`px-6 py-2 font-semibold rounded-md transition-all ${activeTab === 'preview'
                                    ? 'bg-primary text-white shadow-sm'
                                    : 'text-text-light hover:text-text hover:bg-background'
                                    }`}
                                disabled={!!jsonError}
                            >
                                Preview
                            </button>
                        </div>

                        {/* Generate Button */}
                        <div className='relative'>
                            <button
                                onClick={() => setShowGeneratePrompt(!showGeneratePrompt)}
                                disabled={isGenerating}
                                className='px-4 py-2 bg-accent hover:bg-opacity-80 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                title="Generate challenge with AI"
                            >
                                {isGenerating ? '⏳ Generating...' : '✨ Generate'}
                            </button>

                            {/* Generate Prompt Popup */}
                            {showGeneratePrompt && (
                                <div className='absolute right-0 top-full mt-2 w-80 p-3 bg-background border-2 border-accent rounded-lg shadow-lg z-50'>
                                    {isGenerating ? (
                                        <div className='text-center py-4'>
                                            <div className='text-2xl mb-2'>⏳</div>
                                            <p className='text-sm text-text-light'>Generating challenge...</p>
                                            <p className='text-xs text-text-light mt-1'>Using prompt: {customPrompt.substring(0, 50)}{customPrompt.length > 50 ? '...' : ''}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <label className='text-xs font-medium text-text-light mb-1 block'>
                                                Custom instructions (AI will choose type based on your prompt):
                                            </label>
                                            <textarea
                                                value={customPrompt}
                                                onChange={(e) => setCustomPrompt(e.target.value)}
                                                placeholder="e.g., Make an InputBox about strings, create a Match challenge for operators..."
                                                className='w-full h-20 px-2 py-1.5 bg-background-light border border-border rounded text-sm outline-none focus:border-accent resize-none'
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') setShowGeneratePrompt(false)
                                                }}
                                                autoFocus
                                            />
                                            <div className='flex gap-2 mt-2'>
                                                <button
                                                    onClick={handleGenerate}
                                                    className='flex-1 px-3 py-1.5 bg-accent hover:bg-opacity-80 text-white text-xs font-bold rounded transition-colors'
                                                >
                                                    Generate
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowGeneratePrompt(false)
                                                        setCustomPrompt('')
                                                    }}
                                                    className='px-3 py-1.5 bg-background-light hover:bg-border text-text text-xs font-medium rounded transition-colors'
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <Avatar size={"xs"} showRings={false} dropdown={true} />
                    </div>
                </div>

                {/* Content Area */}
                <div className='flex-grow overflow-hidden flex flex-col'>
                    {activeTab === 'code' ? (
                        <CodeView
                            key={editorKey}
                            initialContent={challenge.content}
                            onUpdate={handleEditorUpdate}
                            editorRef={editorRef}
                            jsonError={jsonError}
                        />
                    ) : (
                        <PreviewErrorBoundary key={`preview-${challenge.id}`}>
                            <PreviewView
                                parsedChallenge={parsedChallenge}
                            />
                        </PreviewErrorBoundary>
                    )}
                </div>
            </div>
        </div>
    )
}

// Code View Component
function CodeView({ initialContent, onUpdate, editorRef, jsonError }) {
    const [content, setContent] = useState(initialContent)

    // Update content when initialContent changes (switching challenges)
    useEffect(() => {
        setContent(initialContent)
    }, [initialContent])

    const handleEditorChange = (value) => {
        setContent(value)
        onUpdate(value)
    }

    return (
        <div className='h-full flex flex-col overflow-hidden bg-code-light'>
            <div className='flex-grow overflow-hidden'>
                <Editor
                    height="100%"
                    defaultLanguage="json"
                    value={content}
                    onChange={handleEditorChange}
                    theme="vs-dark"
                    options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        formatOnPaste: true,
                        formatOnType: true,
                        wordWrap: 'on',
                        wrappingIndent: 'indent'
                    }}
                />
            </div>
            {jsonError && (
                <div className='px-4 py-2 bg-warning-darker text-white text-xs font-medium border-t border-warning flex items-center gap-2 flex-shrink-0'>
                    <span>⚠️</span>
                    <span>{jsonError}</span>
                </div>
            )}
        </div>
    )
}

// Preview View Component (simplified - shows single challenge with regen only)
function PreviewView({ parsedChallenge }) {
    const [questionStatus, setQuestionStatus] = useState({
        guessed: false,
        answer: null,
        message: null,
    })
    const [challengeKey, setChallengeKey] = useState(1)
    const answerCheckRef = useRef(null)

    // Reset status when challenge changes (but don't increment key - only regenerate button does that)
    useEffect(() => {
        setQuestionStatus({ guessed: false, answer: null, message: null })
    }, [parsedChallenge])

    const handleRegenerate = () => {
        setQuestionStatus({ guessed: false, answer: null, message: null })
        setChallengeKey(prev => prev + 1)
    }

    // Parse everything in the useMemo - NO separate useEffect that could loop
    const { questionText, questionVariables } = useMemo(() => {
        if (!parsedChallenge || parsedChallenge.type === 'Intro') {
            return {
                questionText: <span className='text-3xl font-bold'>Lesson Intro</span>,
                questionVariables: []
            }
        }

        const questions = parsedChallenge.question || []
        if (!questions.length) {
            return {
                questionText: <span>No question defined</span>,
                questionVariables: []
            }
        }

        try {
            // Parse variables LOCALLY, don't set state
            const rawVars = parsedChallenge.variables || []
            const parsedVars = rawVars.length ? lessonParser.parseValueList(rawVars, true) : []

            const index = Math.floor(Math.random() * questions.length)
            const questionHTML = lessonParser
                .replaceVariablesInText(questions[index], parsedVars)
                .replace(/\*\*(.*?)\*\*/g, '<span class="questionTitleBolded">$1</span>')
                .replace(/\*(.*?)\*/g, '<span class="questionTitleUnderscore">$1</span>')
                .replace(/\`(.*?)\`/g, '<span class="questionTitleCode">$1</span>')

            return {
                questionText: <span dangerouslySetInnerHTML={{ __html: questionHTML }} />,
                questionVariables: parsedVars
            }
        } catch (error) {
            console.error('Question text error:', error)
            return {
                questionText: <span className='text-warning'>Error rendering question text</span>,
                questionVariables: []
            }
        }
    }, [parsedChallenge, challengeKey])

    const challengeComponents = {
        Intro,
        MultiChoice,
        InputBox,
        TextBox,
        MultiChoiceSplit,
        Match,
    }

    const getChallengeComponent = () => {
        if (!parsedChallenge) {
            return (
                <div className='text-text-light text-xl'>
                    No challenge to preview. Check JSON for errors.
                </div>
            )
        }

        const Component = challengeComponents[parsedChallenge.type]
        if (!Component) {
            return (
                <div className='text-warning text-xl'>
                    Unknown challenge type: {parsedChallenge.type}
                </div>
            )
        }

        return (
            <Component
                key={challengeKey}
                questionData={parsedChallenge}
                questionVariables={questionVariables}
                questionText={questionText}
                questionStatus={questionStatus}
                onAnswerCheck={(checkFn) => (answerCheckRef.current = checkFn)}
                onLeave={() => { }}
                onHelpUsed={() => { }}
                manualCheck={async () => {
                    if (answerCheckRef.current) {
                        const result = await answerCheckRef.current()
                        if (result && result.isCorrect !== null) {
                            setQuestionStatus({
                                guessed: true,
                                answer: result.isCorrect,
                                message: result.message || null
                            })
                        }
                    }
                }}
                tag={parsedChallenge.tag}
                currentConceptProgress={1}
                setIntroButtonDisabled={() => { }}
            />
        )
    }

    const handleCheck = async () => {
        if (parsedChallenge?.type === 'Intro') {
            // Intro doesn't need checking
            return
        }

        if (answerCheckRef.current) {
            const result = await answerCheckRef.current()
            if (result && result.isCorrect !== null) {
                setQuestionStatus({
                    guessed: true,
                    answer: result.isCorrect,
                    message: result.message || null
                })
            }
        }
    }

    return (
        <div className='h-full flex flex-col overflow-hidden'>
            {/* Preview Toolbar */}
            <div className='flex items-center justify-between px-4 py-3 border-b-2 border-border bg-background-light flex-shrink-0'>
                <div className='flex items-center gap-3'>
                    <span className='text-text-light text-sm'>
                        {parsedChallenge ? (parsedChallenge.type === 'Intro' ? 'Intro Preview' : 'Challenge Preview') : 'No Preview'}
                    </span>
                </div>
                <div className='flex items-center gap-2'>
                    <button
                        onClick={handleRegenerate}
                        className='px-3 py-1.5 bg-background hover:bg-border text-text text-sm font-medium rounded-lg transition-colors'
                        title="Regenerate challenge with new random values"
                    >
                        🔄 Regen
                    </button>
                </div>
            </div>

            {/* Preview Content - scrollable with max height */}
            <div className='flex-grow overflow-y-auto overflow-x-hidden bg-background'>
                <div className='min-h-full flex items-center justify-center p-6 pb-24'>
                    <div className='w-full max-w-4xl'>
                        {getChallengeComponent()}
                    </div>
                </div>
            </div>

            {/* Preview Controls */}
            {parsedChallenge?.type !== "Intro" && (
                <div className="w-full font-bold py-2 px-4 border-t-2 bg-background border-border flex items-center justify-center flex-shrink-0">
                    <div className='gap-4 flex items-center justify-center w-full py-2'>
                        {!questionStatus["guessed"] && (
                            <button
                                className='border-[3px] w-48 border-border px-2 py-3 rounded-3xl text-border text-2xl'
                                onClick={() => setQuestionStatus({ guessed: false, answer: null, message: null })}
                            >
                                SKIP
                            </button>
                        )}
                        <button
                            className={`${questionStatus["guessed"] ? (questionStatus["answer"] ? "bg-accent shadow-accent-dark" : "bg-warning shadow-warning-dark") : "bg-primary shadow-primary-dark"} w-48 text-center text-background px-2 py-3 rounded-3xl text-3xl shadow-b-md active:shadow-none active:translate-y-1`}
                            onClick={handleCheck}
                        >
                            CHECK
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminCreator
